/**
 * Enhanced custom hook for authentication
 * Provides easy access to auth state and actions
 */

import { useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from './useRedux';
import {
  logout as logoutAction,
  setError,
  clearError,
  setCredentials,
} from '@/store/slices/authSlice';
import { authClient } from '@/services/clients';
import type { LoginRequest, UserData } from 'auth-service';

// The actual API response structure (runtime)
interface LoginResponseData {
  access_token: string;
  refresh_token: string;
  user: UserData;
  permissions?: string[];
}

// Response type for user search
interface UserSearchResponse {
  success: boolean;
  data?: UserData[];
  message?: string;
}

export const useAuth = () => {
  const dispatch = useAppDispatch();
  const { user, accessToken, isAuthenticated, error } = useAppSelector((state) => state.auth);

  /**
   * Login with phone number and password
   */
  const login = useCallback(
    async (credentials: LoginRequest) => {
      try {
        const response = await authClient.auth.login(credentials);

        // Handle both wrapped { data: ... } and direct response formats
        const rawResponse = response as unknown as { data?: LoginResponseData } & LoginResponseData;
        const loginData: LoginResponseData = rawResponse.data ?? rawResponse;

        if (import.meta.env.DEV) {
          console.log('[useAuth] Login successful:', {
            user: loginData.user?.id,
            userName: loginData.user?.name,
            hasToken: !!loginData.access_token,
          });
        }

        dispatch(
          setCredentials({
            accessToken: loginData.access_token,
            refreshToken: loginData.refresh_token,
            user: loginData.user,
          })
        );

        return response;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Login failed';
        dispatch(setError(errorMessage));
        throw err;
      }
    },
    [dispatch]
  );

  /**
   * Logout user
   */
  const logout = useCallback(async () => {
    try {
      await authClient.auth.logout();
    } catch (err) {
      console.error('Logout failed:', err);
    } finally {
      dispatch(logoutAction());
    }
  }, [dispatch]);

  /**
   * Set authentication error
   */
  const setAuthError = useCallback(
    (errorMessage: string) => {
      dispatch(setError(errorMessage));
    },
    [dispatch]
  );

  /**
   * Clear authentication error
   */
  const clearAuthError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  return {
    user,
    accessToken,
    isAuthenticated,
    error,
    login,
    logout,
    setError: setAuthError,
    clearError: clearAuthError,
  };
};

/**
 * Hook for searching users by mobile number
 * Returns the user ID if found, otherwise null
 */
export const useUserSearch = () => {
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const searchByMobile = useCallback(async (mobileNumber: string): Promise<UserData | null> => {
    if (!mobileNumber || mobileNumber.length < 10) {
      return null;
    }

    setIsSearching(true);
    setSearchError(null);

    try {
      const response = (await authClient.users.search({ q: mobileNumber })) as UserSearchResponse;

      if (response.success && response.data && response.data.length > 0) {
        // Find exact match by phone number
        const exactMatch = response.data.find(
          (user) => user.phone_number === mobileNumber || user.phone_number?.endsWith(mobileNumber)
        );
        return exactMatch || response.data[0];
      }

      return null;
    } catch (err) {
      console.error('[useUserSearch] Search failed:', err);
      setSearchError(err instanceof Error ? err.message : 'Search failed');
      return null;
    } finally {
      setIsSearching(false);
    }
  }, []);

  return {
    searchByMobile,
    isSearching,
    searchError,
  };
};
